import React, { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, MouseSensor, } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIcon, QuestionMarkIcon, TrashIcon } from '@fourthwall/icons';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import * as S from './SortableGallery.styled';
import { useMediaQueryContext } from '../../providers/MediaQueryProvider';
import { useModalContext } from '../../providers/ModalProvider';
import { Divider } from '../Divider';
import MobileContextMenu from './MobileContextMenu/MobileContextMenu';
import { Tooltip } from '../Tooltip';
const SortableGalleryElement = React.memo(({ value, isDisabled, inactive, onView, onRemove, onMoveUp, onMoveDown, }) => {
    const media = useMediaQueryContext();
    const { open } = useModalContext();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: value.id,
        disabled: isDisabled,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const handleImageClick = React.useCallback((e) => {
        // Prevent click when dragging ends, very important! Otherwise, after image removal it will call onView
        if (e.detail === 0)
            return;
        if (!media.tablet) {
            open(MobileContextMenu, {
                onView: () => onView?.(value),
                onMoveUp,
                onMoveDown,
                onDelete: () => onRemove?.(value),
            });
        }
        else if (onView) {
            onView(value);
        }
    }, [media.tablet, onMoveDown, onMoveUp, onRemove, onView, open, value]);
    const handleTrashClick = React.useCallback((e) => {
        e.stopPropagation();
        onRemove?.(value);
    }, [onRemove, value]);
    if (value.isUploading) {
        return (React.createElement(S.Item, null,
            React.createElement(S.LoaderContainer, null,
                React.createElement("div", null, "Uploading..."),
                React.createElement(Loader, null))));
    }
    return (React.createElement(S.Item, { ref: setNodeRef, style: style, disabled: isDisabled, ...attributes, ...listeners },
        React.createElement(S.ImageContainer, { disabled: isDisabled, onClick: isDisabled ? undefined : handleImageClick },
            React.createElement(S.Image, { src: value.url, inactive: inactive }),
            onRemove && (React.createElement(S.TrashButtonWrapper, null,
                React.createElement(S.TrashButton, { onClick: handleTrashClick },
                    React.createElement(Icon, { component: TrashIcon, height: 12, width: 12 })))),
            !isDisabled && (React.createElement(S.DragAndDropAnchorWrapper, null,
                React.createElement(S.DragAndDropAnchorButton, null,
                    React.createElement(Icon, { component: DragIcon, height: 12, width: 12 })))))));
});
export default SortableGalleryElement;
const SortableGalleryContainer = ({ data, uploadZone, inactiveStartIndex, disabled, onView, onRemove, onSortChange, }) => {
    const media = useMediaQueryContext();
    const activeItems = inactiveStartIndex ? data.slice(0, inactiveStartIndex) : data;
    const inactiveItems = inactiveStartIndex ? data.slice(inactiveStartIndex) : [];
    let columnCount;
    if (inactiveStartIndex && media.tablet) {
        columnCount = activeItems.length >= 7 ? 6 : 4;
    }
    const handleMoveUp = (index) => {
        const canMoveUp = index > 0;
        if (canMoveUp && onSortChange) {
            onSortChange(index, index - 1);
        }
    };
    const handleMoveDown = (index) => {
        const canMoveDown = index < data.length - 1;
        if (canMoveDown && onSortChange) {
            onSortChange(index, index + 1);
        }
    };
    return (React.createElement(S.Container, { columns: columnCount },
        React.createElement(SortableContext, { items: data.map((item) => item.id) },
            activeItems.length === 0 && uploadZone,
            activeItems.length > 0 &&
                activeItems.map((value, index) => (React.createElement(S.ItemWrapper, { key: value.id },
                    React.createElement(S.Placeholder, null),
                    React.createElement(SortableGalleryElement, { value: value, disabled: disabled || !media.tablet, isDisabled: disabled, onView: onView, onRemove: onRemove, onMoveDown: () => handleMoveDown(index), onMoveUp: () => handleMoveUp(index) })))),
            uploadZone && activeItems.length !== 0 && inactiveItems.length === 0 && (React.createElement(S.UploadZone, null, uploadZone)),
            inactiveStartIndex && inactiveItems.length > 0 && (React.createElement(S.Divider, null,
                React.createElement(Divider, { label: React.createElement(S.DividerText, null,
                        "Inactive photos",
                        ' ',
                        React.createElement(Tooltip, { reference: React.createElement(Icon, { component: QuestionMarkIcon, height: 12 }), fluid: true },
                            React.createElement("div", { style: { width: '220px' } }, "You can feature up to 12 photos in your product gallery. The remaining photos (marked as inactive) won't be displayed."))) }))),
            inactiveStartIndex &&
                inactiveItems.length > 0 &&
                inactiveItems.map((value, elementIndex) => {
                    const index = elementIndex + inactiveStartIndex;
                    return (React.createElement(S.ItemWrapper, { key: value.id },
                        React.createElement(S.Placeholder, null),
                        React.createElement(SortableGalleryElement, { inactive: true, value: value, disabled: disabled || !media.tablet, isDisabled: disabled, onView: onView, onRemove: onRemove, onMoveDown: () => handleMoveDown(index), onMoveUp: () => handleMoveUp(index) })));
                }),
            uploadZone && inactiveItems.length > 0 && React.createElement(S.UploadZone, null, uploadZone))));
};
export const SortableGallery = ({ data, uploadZone, disabled = false, inactiveStartIndex, onView, onRemove, onSortChange, }) => {
    const [isDragged, setIsDragged] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: disabled ? Infinity : 5,
        },
    }), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }), useSensor(MouseSensor, {
        activationConstraint: {
            distance: disabled ? Infinity : 5,
        },
    }));
    const handleDragStart = () => {
        if (disabled)
            return;
        setIsDragged(true);
    };
    const handleDragEnd = (event) => {
        if (disabled)
            return;
        const { active, over } = event;
        setIsDragged(false);
        if (active?.id && over?.id && active?.id !== over?.id) {
            const oldIndex = data.findIndex((item) => item?.id === active?.id);
            const newIndex = data.findIndex((item) => item?.id === over?.id);
            if (oldIndex !== -1 && newIndex !== -1 && onSortChange) {
                onSortChange(oldIndex, newIndex);
            }
        }
    };
    return (React.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragEnd: handleDragEnd },
        React.createElement(SortableGalleryContainer, { data: data, uploadZone: uploadZone, inactiveStartIndex: inactiveStartIndex, disabled: disabled, isDragged: isDragged, onView: onView, onRemove: onRemove, onSortChange: onSortChange })));
};
SortableGallery.UploadZoneWrapper = S.UploadZoneWrapper;
